import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useTheme } from '../ThemeContext';
import { wakeUpServer } from '../utils/api';

const STATS = [
  { value: '94.68%', label: 'Accuracy', sub: 'On TE1.csv test set' },
  { value: '0.201ms', label: 'MAE Score', sub: 'Mean Absolute Error' },
  { value: '40K+', label: 'Records', sub: 'KTH ExPECA testbed' },
  { value: '10×', label: 'Recursive Steps', sub: 'Hybrid forecast' },
];

const QUICK_LINKS = [
  { path: '/industries',  title: 'Industry Applications', desc: 'Robotics, V2X, Remote Surgery, SLA monitoring and more', color: '#00d4ff' },
  { path: '/research',   title: 'Research Data', desc: 'KTH ExPECA testbed, 40K traces, protocol layer analysis', color: '#00cc66' },
  { path: '/benchmark',   title: 'Model Benchmark', desc: 'ARIMA vs RNN vs LSTM vs Hybrid LSTM comparison', color: '#f0a500' },
  { path: '/architecture',  title: 'Tech Architecture', desc: 'ONNX Runtime, Flask, React full-stack pipeline', color: '#9966ff' },
];

function SignalCanvas({ dark }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, waves = [], towers = [], particles = [], t = 0, raf;

    function resize() {
      W = canvas.offsetWidth; H = canvas.offsetHeight;
      canvas.width = W; canvas.height = H;
      towers = [];
      const count = Math.max(3, Math.floor(W / 160));
      for (let i = 0; i < count; i++) {
        towers.push({
          x: (i + 0.5) * (W / count) + (Math.random() - 0.5) * 30,
          y: H - 30 - Math.random() * 20,
          h: 25 + Math.random() * 18,
          phase: Math.random() * Math.PI * 2,
          freq: 0.7 + Math.random() * 0.7,
        });
      }
      waves = []; particles = [];
    }

    function draw() {
      raf = requestAnimationFrame(draw);
      t += 0.016;
      ctx.clearRect(0, 0, W, H);
      ctx.strokeStyle = dark ? 'rgba(0,212,255,0.05)' : 'rgba(0,120,200,0.06)';
      ctx.lineWidth = 0.5;
      for (let x = 0; x < W; x += 55) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
      for (let y = 0; y < H; y += 55) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

      towers.forEach(tw => {
        ctx.strokeStyle = dark ? 'rgba(0,212,255,0.3)' : 'rgba(0,100,200,0.4)';
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(tw.x, tw.y); ctx.lineTo(tw.x, tw.y - tw.h); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(tw.x - 9, tw.y - tw.h + 7); ctx.lineTo(tw.x, tw.y - tw.h); ctx.lineTo(tw.x + 9, tw.y - tw.h + 7); ctx.stroke();
        if (Math.sin(t * tw.freq + tw.phase) > 0.88 && waves.length < 80) {
          waves.push({ x: tw.x, y: tw.y - tw.h, r: 0, alpha: dark ? 0.5 : 0.4, speed: 1.0 + Math.random() * 0.9 });
        }
        const on = Math.sin(t * tw.freq * 2 + tw.phase) > 0;
        ctx.fillStyle = on ? (dark ? 'rgba(0,212,255,0.95)' : 'rgba(0,100,200,0.85)') : (dark ? 'rgba(0,212,255,0.12)' : 'rgba(0,100,200,0.1)');
        ctx.beginPath(); ctx.arc(tw.x, tw.y - tw.h - 5, 3, 0, Math.PI * 2); ctx.fill();
      });

      waves = waves.filter(w => {
        w.r += w.speed; w.alpha -= 0.007;
        if (w.alpha <= 0 || w.r > 200) return false;
        ctx.beginPath(); ctx.arc(w.x, w.y, w.r, 0, Math.PI * 2);
        ctx.strokeStyle = dark ? `rgba(0,212,255,${w.alpha})` : `rgba(0,100,200,${w.alpha})`;
        ctx.lineWidth = 1; ctx.stroke();
        if (w.r > 20) {
          ctx.beginPath(); ctx.arc(w.x, w.y, w.r * 0.6, 0, Math.PI * 2);
          ctx.strokeStyle = dark ? `rgba(0,150,255,${w.alpha * 0.4})` : `rgba(0,80,200,${w.alpha * 0.3})`;
          ctx.lineWidth = 0.5; ctx.stroke();
        }
        return true;
      });

      if (Math.random() < 0.05 && particles.length < 100) {
        const s = towers[Math.floor(Math.random() * towers.length)];
        const d = towers[Math.floor(Math.random() * towers.length)];
        if (s && d && s !== d) particles.push({ sx: s.x, sy: s.y - s.h, ex: d.x, ey: d.y - d.h, prog: 0, speed: 0.004 + Math.random() * 0.007, trail: [] });
      }
      particles = particles.filter(p => {
        p.prog += p.speed;
        if (p.prog >= 1) return false;
        const q = p.prog, cy = Math.min(p.sy, p.ey) - 70;
        const bx = (1-q)*(1-q)*p.sx + 2*(1-q)*q*((p.sx+p.ex)/2) + q*q*p.ex;
        const by = (1-q)*(1-q)*p.sy + 2*(1-q)*q*cy + q*q*p.ey;
        p.trail.push({ x: bx, y: by });
        if (p.trail.length > 14) p.trail.shift();
        if (p.trail.length > 1) {
          ctx.beginPath(); ctx.moveTo(p.trail[0].x, p.trail[0].y);
          for (let i = 1; i < p.trail.length; i++) ctx.lineTo(p.trail[i].x, p.trail[i].y);
          ctx.strokeStyle = dark ? 'rgba(0,212,255,0.55)' : 'rgba(0,100,200,0.45)';
          ctx.lineWidth = 1.5; ctx.stroke();
        }
        ctx.beginPath(); ctx.arc(bx, by, 3, 0, Math.PI * 2);
        ctx.fillStyle = dark ? 'rgba(0,212,255,0.95)' : 'rgba(0,100,200,0.85)'; ctx.fill();
        ctx.beginPath(); ctx.arc(bx, by, 7, 0, Math.PI * 2);
        ctx.fillStyle = dark ? 'rgba(0,212,255,0.1)' : 'rgba(0,100,200,0.08)'; ctx.fill();
        return true;
      });
    }

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize(); draw();
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [dark]);

  return <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />;
}

export default function Home() {
  const navigate = useNavigate();
  const { dark } = useTheme();
  const [status, setStatus] = useState('waking');
  const [inView, setInView] = useState({});
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const sectionRefs = useRef({});

  useEffect(() => { wakeUpServer().then(ok => setStatus(ok ? 'ready' : 'offline')); }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) setInView(p => ({ ...p, [e.target.dataset.sec]: true })); });
    }, { threshold: 0.1 });
    Object.values(sectionRefs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const ref = (k) => el => { sectionRefs.current[k] = el; };
  const handleMouseMove = useCallback((e) => { setCursorPos({ x: e.clientX, y: e.clientY }); }, []);

  const bg      = dark ? '#060c18' : '#f4f8ff';
  const border  = dark ? 'rgba(0,212,255,0.13)' : 'rgba(0,120,200,0.14)';
  const accent  = dark ? '#00d4ff' : '#0078c8';
  const text1   = dark ? '#e8f4ff' : '#0a1e3d';
  const text2   = dark ? '#93b8d8' : '#2a4a6a';
  const text3   = dark ? '#557090' : '#5878a0';
  const gridC   = dark ? 'rgba(0,212,255,0.04)' : 'rgba(0,120,200,0.05)';
  const cardBg  = dark ? 'rgba(0,212,255,0.04)' : 'rgba(255,255,255,0.97)';
  const cardShad= dark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 4px 24px rgba(0,100,200,0.09)';
  const statusC = status === 'ready' ? '#00cc66' : status === 'offline' ? '#ff4455' : '#ffaa00';
  const btnClip = 'polygon(10px 0%,100% 0%,calc(100% - 10px) 100%,0% 100%)';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Inter:wght@300;400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        .cursor-ring{width:22px;height:22px;border:1.5px solid ${accent};border-radius:50%;position:fixed;pointer-events:none;z-index:9999;transform:translate(-50%,-50%);}
        .cursor-ring::after{content:'';position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:4px;height:4px;background:${accent};border-radius:50%;}
        .hroot{background:${bg};color:${text1};font-family:'Inter',sans-serif;min-height:100vh;padding-top:60px;overflow-x:hidden;transition:background 0.35s;cursor:none;}
        .hroot::before{content:'';position:fixed;inset:0;background-image:linear-gradient(${gridC} 1px,transparent 1px),linear-gradient(90deg,${gridC} 1px,transparent 1px);background-size:52px 52px;pointer-events:none;z-index:0;}

        /* ── HERO ── */
        .hero{position:relative;z-index:1;min-height:calc(100vh - 60px);display:flex;align-items:center;justify-content:center;padding:60px 24px;overflow:hidden;background:${dark?'#060c18':'#f4f8ff'};}
        .hero-overlay{position:absolute;inset:0;background:${dark?'linear-gradient(160deg,rgba(6,12,24,0.55) 0%,rgba(0,40,80,0.35) 100%)':'linear-gradient(160deg,rgba(244,248,255,0.65) 0%,rgba(200,225,255,0.45) 100%)'};pointer-events:none;z-index:1;}
        .rings{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);pointer-events:none;z-index:1;}
        .rng{position:absolute;border-radius:50%;border:1px solid ${dark?'rgba(0,212,255,0.1)':'rgba(0,120,200,0.08)'};top:50%;left:50%;transform:translate(-50%,-50%);animation:ringOut 4.5s ease-out infinite;}
        .rng:nth-child(2){animation-delay:1.1s;}.rng:nth-child(3){animation-delay:2.2s;}.rng:nth-child(4){animation-delay:3.3s;}
        @keyframes ringOut{0%{opacity:.7;transform:translate(-50%,-50%) scale(.15);}100%{opacity:0;transform:translate(-50%,-50%) scale(1);}}
        .hero-content{position:relative;z-index:2;max-width:720px;text-align:center;width:100%;}
        .hero-eyebrow{font-size:11px;font-weight:600;letter-spacing:3px;color:${accent};text-transform:uppercase;border:1px solid ${dark?'rgba(0,212,255,0.28)':'rgba(0,120,200,0.32)'};padding:5px 16px;border-radius:20px;display:inline-block;margin-bottom:24px;background:${dark?'rgba(0,212,255,0.09)':'rgba(0,120,200,0.08)'};}
        .hero-h1{font-family:'Orbitron',monospace;font-size:clamp(26px,6vw,62px);font-weight:900;line-height:1.08;letter-spacing:-1px;margin-bottom:10px;color:${text1};}
        .hero-h1 em{font-style:normal;color:${accent};}
        .hero-sub{font-size:clamp(14px,2vw,17px);color:${text2};max-width:540px;line-height:1.78;margin:18px auto 32px;}
        .hero-btns{display:flex;gap:12px;justify-content:center;flex-wrap:wrap;margin-bottom:24px;}
        .btn-main{font-family:'Inter',sans-serif;font-size:13px;font-weight:700;letter-spacing:1px;text-transform:uppercase;background:${accent};color:#fff;border:none;padding:13px 30px;cursor:none;clip-path:${btnClip};transition:all 0.2s;box-shadow:0 4px 22px ${dark?'rgba(0,212,255,0.35)':'rgba(0,120,200,0.35)'};}
        .btn-main:hover{filter:brightness(1.12);transform:translateY(-2px);}
        .btn-out{font-family:'Inter',sans-serif;font-size:13px;font-weight:700;letter-spacing:1px;text-transform:uppercase;background:${dark?'rgba(255,255,255,0.07)':'rgba(255,255,255,0.92)'};color:${dark?'#d8f0ff':'#0a1e3d'};border:2px solid ${dark?'rgba(200,230,255,0.45)':'rgba(10,30,60,0.5)'};padding:12px 30px;cursor:none;transition:all 0.2s;border-radius:2px;backdrop-filter:blur(10px);}
        .btn-out:hover{border-color:${accent};color:${accent};background:${dark?'rgba(0,212,255,0.1)':'rgba(0,120,200,0.07)'};transform:translateY(-2px);}
        .status-row{display:inline-flex;align-items:center;gap:7px;font-size:11px;font-weight:500;letter-spacing:1px;color:${text2};}
        .sdot{width:6px;height:6px;border-radius:50%;background:${statusC};animation:sdot 2s infinite;}
        @keyframes sdot{0%,100%{opacity:1;}50%{opacity:.35;}}

        /* ── STATS ── */
        .stats-sec{position:relative;z-index:1;padding:0 24px 56px;max-width:1060px;margin:0 auto;}
        .stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:${dark?'rgba(0,212,255,0.1)':'rgba(0,120,200,0.12)'};border:1px solid ${border};}
        .sc{background:${dark?'#060c18':'#fff'};padding:28px 20px;transition:background 0.2s;position:relative;overflow:hidden;}
        .sc::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:${accent};opacity:0;transition:opacity 0.25s;}
        .sc:hover{background:${dark?'rgba(0,212,255,0.05)':'rgba(0,120,200,0.04)'};}
        .sc:hover::before{opacity:1;}
        .sv{font-family:'Orbitron',monospace;font-size:clamp(22px,3vw,32px);font-weight:900;color:${accent};line-height:1;margin-bottom:6px;}
        .sl{font-size:13px;font-weight:700;color:${text1};margin-bottom:2px;}
        .ss{font-size:11px;color:${text3};}

        /* ── SECTIONS ── */
        .sec{position:relative;z-index:1;padding:60px 24px;max-width:1060px;margin:0 auto;}
        .fade-in{opacity:0;transform:translateY(24px);transition:opacity 0.6s ease,transform 0.6s ease;}
        .fade-in.visible{opacity:1;transform:none;}
        .eyebrow{font-size:11px;font-weight:600;letter-spacing:3px;color:${accent};text-transform:uppercase;margin-bottom:10px;}
        .sec-title{font-family:'Orbitron',monospace;font-size:clamp(20px,3.5vw,34px);font-weight:700;color:${text1};margin-bottom:12px;}
        .sec-body{font-size:15px;color:${text2};line-height:1.82;max-width:600px;margin-bottom:36px;}

        /* ── QUICK LINKS ── */
        .ql-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:16px;}
        .ql-card{border:1px solid ${border};background:${cardBg};padding:24px;border-radius:8px;box-shadow:${cardShad};cursor:none;transition:all 0.3s;position:relative;overflow:hidden;text-decoration:none;display:block;}
        .ql-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;opacity:0;transition:opacity 0.3s;}
        .ql-card:hover{transform:translateY(-4px);}
        .ql-card:hover::before{opacity:1;}

        /* ── HOW ── */
        .steps{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;}
        .step{border:1px solid ${border};background:${cardBg};padding:26px 20px;border-radius:6px;position:relative;overflow:hidden;box-shadow:${cardShad};transition:all 0.3s;cursor:none;}
        .step:hover{border-color:${dark?'rgba(0,212,255,0.4)':'rgba(0,120,200,0.4)'};transform:translateY(-4px);}
        .step::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,${accent},transparent);}
        .snum{font-family:'Orbitron',monospace;font-size:38px;font-weight:900;color:${dark?'rgba(0,212,255,0.1)':'rgba(0,120,200,0.09)'};line-height:1;margin-bottom:14px;}
        .stitle{font-size:15px;font-weight:700;color:${text1};margin-bottom:8px;}
        .sdesc{font-size:13px;color:${text2};line-height:1.72;}

        /* ── CTA ── */
        .cta-sec{position:relative;z-index:1;overflow:hidden;padding:72px 24px;text-align:center;border-top:1px solid ${border};border-bottom:1px solid ${border};}
        .cta-img{position:absolute;inset:0;background-image:url('/pic1.jpg');background-size:cover;background-position:center;filter:${dark?'brightness(0.1) saturate(0.5)':'brightness(0.08) saturate(0.4)'};}
        .marquee{position:absolute;top:10px;left:0;right:0;overflow:hidden;font-family:'Orbitron',monospace;font-size:10px;letter-spacing:3px;color:${dark?'rgba(0,212,255,0.18)':'rgba(0,80,180,0.12)'};white-space:nowrap;animation:mq 22s linear infinite;}
        @keyframes mq{0%{transform:translateX(100vw);}100%{transform:translateX(-200%);}}
        .cta-inner{position:relative;z-index:1;}
        .cta-h{font-family:'Orbitron',monospace;font-size:clamp(18px,3.5vw,34px);font-weight:700;color:${text1};margin-bottom:12px;}
        .cta-s{font-size:15px;color:${text2};margin-bottom:28px;}

        /* ── RESPONSIVE ── */
        @media(max-width:768px){
          .stats-grid{grid-template-columns:repeat(2,1fr);}
          .steps{grid-template-columns:1fr;}
          .ql-grid{grid-template-columns:1fr;}
          .hero{min-height:auto;padding:48px 20px 40px;}
          .sec{padding:48px 20px;}
          .stats-sec{padding:0 20px 48px;}
        }
        @media(max-width:480px){
          .stats-grid{grid-template-columns:repeat(2,1fr);}
          .hero-btns{flex-direction:column;align-items:center;}
          .btn-main,.btn-out{width:100%;max-width:280px;}
        }
      `}</style>

      <div className="cursor-ring" style={{ left: cursorPos.x, top: cursorPos.y }} />

      <div className="hroot" onMouseMove={handleMouseMove}>

        {/* ── HERO ── */}
        <section className="hero">
          <SignalCanvas dark={dark} />
          <div className="hero-overlay" />
          <div className="rings">
            {[140, 270, 410, 560].map((s, i) => (<div key={i} className="rng" style={{ width: s, height: s }} />))}
          </div>
          <div className="hero-content">
            <div className="hero-eyebrow">KTH ExPECA Testbed · Hybrid LSTM · ONNX Runtime</div>
            <h1 className="hero-h1">Predict 5G<br /><em>Network Delays</em></h1>
            <p className="hero-sub">
              A full-stack AI platform trained on 40,000+ real 5G packet traces. Hybrid LSTM achieves 94.68% accuracy — outperforming RNN, ARIMA and pure recursive approaches.
            </p>
            <div className="hero-btns">
              <button className="btn-main" onClick={() => navigate('/predict')}>Run Prediction</button>
              <button className="btn-out" onClick={() => navigate('/about')}>Research Details</button>
            </div>
            <div className="status-row">
              <div className="sdot" />
              {status === 'ready' ? 'Server Online · Ready' : status === 'offline' ? 'Server Offline' : 'Initializing server...'}
            </div>
          </div>
        </section>

        {/* ── STATS ── */}
        <section className="stats-sec">
          <div className="stats-grid">
            {STATS.map(s => (
              <div className="sc" key={s.label}>
                <div className="sv">{s.value}</div>
                <div className="sl">{s.label}</div>
                <div className="ss">{s.sub}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── EXPLORE ── */}
        <section className="sec" data-sec="ql" ref={ref('ql')}>
          <div className={`fade-in${inView.ql ? ' visible' : ''}`}>
            <div className="eyebrow">{'// Explore the Platform'}</div>
            <div className="sec-title">Deep Dive Into the Research</div>
            <p className="sec-body">The platform includes dedicated sections covering every aspect — from industry applications to the technical architecture.</p>
            <div className="ql-grid">
              {QUICK_LINKS.map((ql, i) => (
                <div
                  key={ql.path}
                  className="ql-card"
                  onClick={() => navigate(ql.path)}
                  style={{
                    transitionDelay: `${i * 0.08}s`,
                    opacity: inView.ql ? 1 : 0,
                    transform: inView.ql ? 'none' : 'translateY(20px)',
                    transition: 'opacity 0.5s ease, transform 0.5s ease, border-color 0.3s',
                    borderColor: border,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = ql.color + '55'; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.querySelector('.ql-before').style.opacity = '1'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.transform = 'none'; e.currentTarget.querySelector('.ql-before').style.opacity = '0'; }}
                >
                  <div className="ql-before" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${ql.color},transparent)`, opacity: 0, transition: 'opacity 0.3s' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
                    <span style={{ fontSize: 26 }}>{ql.icon}</span>
                    <div style={{ fontFamily: "'Orbitron',monospace", fontSize: 13, fontWeight: 700, color: text1 }}>{ql.title}</div>
                  </div>
                  <div style={{ fontSize: 13, color: text2, lineHeight: 1.6 }}>{ql.desc}</div>
                  <div style={{ marginTop: 14, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: ql.color, textTransform: 'uppercase' }}>Explore →</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="sec" data-sec="how" ref={ref('how')} style={{ background: dark ? 'rgba(0,212,255,0.02)' : 'rgba(0,120,200,0.02)', maxWidth: '100%', padding: '60px 24px' }}>
          <div style={{ maxWidth: 1060, margin: '0 auto' }}>
            <div className={`fade-in${inView.how ? ' visible' : ''}`}>
              <div className="eyebrow">{'// How It Works'}</div>
              <div className="sec-title">Three steps to insight</div>
              <div className="steps">
                {[
                  { num: '01', title: 'Upload CSV Trace', desc: 'Drop your 5G packet trace file with SN and IP Delay (ms) columns. Minimum 30 rows required for a valid prediction window.' },
                  { num: '02', title: 'Hybrid LSTM Inference', desc: 'ONNX-optimized model runs block-wise recursive prediction — 10 steps forward, reset with real data, repeat. Zero TensorFlow overhead.' },
                  { num: '03', title: 'Charts + PDF Report', desc: 'Get interactive actual vs predicted charts, residual error analysis, 6 summary metrics, and a downloadable PDF report.' },
                ].map((s, i) => (
                  <div className="step" key={s.num} style={{ transitionDelay: inView.how ? `${i * 0.12}s` : '0s' }}>
                    <div className="snum">{s.num}</div>
                    <div className="stitle">{s.title}</div>
                    <div className="sdesc">{s.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <div className="cta-sec" data-sec="cta" ref={ref('cta')}>
          <div className="cta-img" />
          <div className="marquee">5G DELAY AI · HYBRID LSTM · KTH EXPECA · ONNX RUNTIME · 94.68% ACCURACY · FRAME ALIGNMENT DELAY · RLC LAYER · TELECOM QOS · </div>
          <div className={`cta-inner fade-in${inView.cta ? ' visible' : ''}`}>
            <div className="cta-h">Ready to predict your network delays?</div>
            <div className="cta-s">Upload any CSV with SN and IP Delay columns. Instant results, PDF report included.</div>
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn-main" onClick={() => navigate('/predict')}>Start Prediction</button>
              <button className="btn-out" onClick={() => navigate('/contact')}>Contact Us</button>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
